import React, { useState, useRef, useEffect, useLayoutEffect } from "react";

interface PopoverProps {
  label: any;
  children?: React.ReactNode;
  position?: "top" | "bottom" | "left" | "right";
  className?: string;
}

const Popover: React.FC<PopoverProps> = ({
  label,
  children,
  position = "bottom",
  className,
}) => {
  const [open, setOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [computedPosition, setComputedPosition] = useState<
    "top" | "bottom" | "left" | "right"
  >(position);
  const [maxHeight, setMaxHeight] = useState<number>();

  // Optional: Hide popover when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    }
    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open]);

  useEffect(() => {
    setComputedPosition(position);
  }, [position]);

  useLayoutEffect(() => {
    if (!open) {
      return;
    }

    const measure = () => {
      const popoverEl = popoverRef.current;
      const contentEl = contentRef.current;

      if (!popoverEl || !contentEl) {
        return;
      }

      const margin = 8;
      const popoverRect = popoverEl.getBoundingClientRect();
      const contentHeight = contentEl.scrollHeight;
      const availableBottom = window.innerHeight - popoverRect.bottom - margin;
      const availableTop = popoverRect.top - margin;

      let nextPosition: "top" | "bottom" | "left" | "right" = position;

      if (position === "bottom") {
        nextPosition =
          contentHeight > availableBottom && availableTop > availableBottom
            ? "top"
            : "bottom";
      } else {
        nextPosition =
          contentHeight > availableTop && availableBottom > availableTop
            ? "bottom"
            : "top";
      }

      setComputedPosition((current) =>
        current === nextPosition ? current : nextPosition
      );

      const availableSpace =
        nextPosition === "bottom" ? availableBottom : availableTop;
      const fallbackSpace = window.innerHeight - 2 * margin;
      const safeSpace = Math.max(0, availableSpace);
      const limitedSpace = safeSpace > 0 ? safeSpace : fallbackSpace;

      setMaxHeight(
        contentHeight > limitedSpace && limitedSpace > 0
          ? limitedSpace
          : undefined
      );
    };

    let frame = requestAnimationFrame(measure);

    const handleWindowChange = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(measure);
    };

    window.addEventListener("resize", handleWindowChange);
    window.addEventListener("scroll", handleWindowChange, true);

    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("resize", handleWindowChange);
      window.removeEventListener("scroll", handleWindowChange, true);
    };
  }, [open, position]);

  return (
    <div className={`relative ${className}`} ref={popoverRef}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full" // Todo: verify other places before release.
      >
        {label}
      </button>
      <div
        className={`absolute ${
          computedPosition !== "top" ? "top-full mt-2" : "bottom-full mb-2"
        } right-0 w-48 min-h-10 p-1 bg-white border border-gray-200 shadow shadow-black/5 rounded-lg z-50 transition-opacity duration-300 ${
          open ? "visible opacity-100" : "invisible opacity-0"
        }`}
        style={{ maxHeight, overflowY: "auto" }}
        ref={contentRef}
        onClick={() => setOpen(false)}
      >
        {children}
      </div>
    </div>
  );
};

export default Popover;
